import { Action, createAction, handleActions } from 'redux-actions'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import {
  fetchDebitCardTransactionWithBenefit,
  IDebitCardMonthlyTransactionHistoryWithBriefRequest,
  IDebitCardMonthlyTransactionHistoryWithBriefResponse,
  IMonthlyBrief,
} from '@/api/DebitCardApi'
import { IPageInfoResponse } from '@/domains/CommonDomain'
import {
  DebitCardTransactionFilterTypeCode,
  DebitCardTransactionType,
  FILTER_TYPE_BY_TRANSACTION,
  HistoryListRequestRowCount,
  IDebitCardTransaction,
  OrderingTypeCode,
} from '@/domains/DebitCardDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { loadingActions } from '@/modules/common/loading'
import { createFetchAction, createPostAction, createSaga } from '@/utils/actionUtils'
import { IYearMonthInfo, IYearMonthInfoResult } from '@/utils/bridgeUtils/features/getYearMonth'
import { getFormattedDateByJakarta, getJakartaDate } from '@/utils/dateUtils'
import { getFormattedYearMonth } from '@/utils/formatUtils'
import { apiFailureReducer, apiSuccessReducer, increasePage } from '@/utils/reducerUtils'

export interface IFilter {
  type: string
  sort: string
}

export interface IDebitCardHistoryView {
  filter: IFilter
  selectedYearMonth: IYearMonthInfo
}

export interface IDebitCardHistoryState
  extends IDebitCardMonthlyTransactionHistoryWithBriefResponse {
  view: IDebitCardHistoryView
}

export interface IHistoryList {
  [date: string]: IDebitCardTransaction[]
}

export const defaultPageInfo: IPageInfoResponse = {
  curPage: 1,
  totCnt: 0,
  totPage: 0,
}

const defaultMonthlyBrief: IMonthlyBrief = {
  totalTransactionAmount: '',
  totalBenefitFeeFreeAmount: '',
  totalBenefitCashBackAmount: '',
  totalBenefitAmount: '',
}

export const defaultView: IDebitCardHistoryView = {
  filter: {
    type: DebitCardTransactionFilterTypeCode.ALL,
    sort: OrderingTypeCode.LATEST,
  },
  selectedYearMonth: {
    year: getJakartaDate().getFullYear(),
    month: getJakartaDate().getMonth() + 1,
  },
}

const initialState: IDebitCardHistoryState = {
  debitCardNo: '',
  debitCardTransactionList: [],
  pageInfo: defaultPageInfo,
  monthlyBrief: defaultMonthlyBrief,
  view: defaultView,
}

export const DEBIT_CARD_HISTORY = 'DebitCardHistory'
export const DEBIT_CARD_HISTORY_MORE = `${DEBIT_CARD_HISTORY}/more`
export const DEBIT_CARD_HISTORY_RELOAD = `${DEBIT_CARD_HISTORY}/reload`
export const SET_FILTER = `${DEBIT_CARD_HISTORY}/setFilter`
export const INCREASE_PAGE = `${DEBIT_CARD_HISTORY}/increasePage`
export const RESET_PAGE = `${DEBIT_CARD_HISTORY}/resetPage`
export const CHANGE_YEAR_MONTH = `${DEBIT_CARD_HISTORY}/changeYearMonth`
export const SET_VIEW = `${DEBIT_CARD_HISTORY}/setView`

export const debitCardHistoryActions = {
  setFilter: createAction(SET_FILTER),
  increasePage: createAction(INCREASE_PAGE),
  resetPage: createAction(RESET_PAGE),
  changeYearMonth: createAction(CHANGE_YEAR_MONTH),
  setView: createAction(SET_VIEW),
}

export const debitCardHistoryAsync = createFetchAction(DEBIT_CARD_HISTORY)
export const debitCardHistoryMoreAsync = createFetchAction(DEBIT_CARD_HISTORY_MORE)
export const debitCardHistoryReloadAsync = createPostAction(DEBIT_CARD_HISTORY_RELOAD)

export const reducer = {
  [debitCardHistoryAsync.SUCCESS]: apiSuccessReducer,
  [debitCardHistoryAsync.FAILURE]: apiFailureReducer,
  [debitCardHistoryReloadAsync.SUCCESS]: apiSuccessReducer,
  [debitCardHistoryReloadAsync.FAILURE]: apiFailureReducer,
  [debitCardHistoryMoreAsync.SUCCESS]: (state: IDebitCardHistoryState, action: Action<any>) => {
    const { debitCardTransactionList = [], pageInfo = state.pageInfo } = action.payload

    return {
      ...state,
      ...action.payload,
      debitCardTransactionList: state.debitCardTransactionList.concat(debitCardTransactionList),
      pageInfo: {
        ...state.pageInfo,
        ...pageInfo,
      },
    }
  },
  [debitCardHistoryMoreAsync.FAILURE]: apiFailureReducer,
  [SET_FILTER]: (state: IDebitCardHistoryState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      filter: {
        ...state.view.filter,
        ...action.payload,
      },
    },
  }),
  [INCREASE_PAGE]: increasePage,
  [RESET_PAGE]: (state: IDebitCardHistoryState) => ({
    ...state,
    pageInfo: {
      ...state.pageInfo,
      page: 1,
    },
  }),
  [SET_VIEW]: (state: IDebitCardHistoryState, action: Action<any>) => {
    return {
      ...state,
      view: {
        ...state.view,
        ...action.payload,
      },
    }
  },
}

export const debitCardHistorySelector = {
  debitCardNo: (state: IRootState) => oc(state[DEBIT_CARD_HISTORY]).debitCardNo(''),
  pageInfo: (state: IRootState) => oc(state[DEBIT_CARD_HISTORY]).pageInfo(defaultPageInfo),
  view: (state: IRootState) => oc(state[DEBIT_CARD_HISTORY]).view(defaultView),
  monthlyBrief: (state: IRootState) =>
    oc(state[DEBIT_CARD_HISTORY]).monthlyBrief(defaultMonthlyBrief),
  historyList: (state: IRootState) => {
    const transactionList = oc(state[DEBIT_CARD_HISTORY]).debitCardTransactionList([])
    const { filter } = debitCardHistorySelector.view(state)
    const isAll = filter.type === DebitCardTransactionFilterTypeCode.ALL
    const debitCardTransactionTypeCodeList = Object.values(DebitCardTransactionType)

    return transactionList.reduce((list: IHistoryList, item) => {
      const { transactionDate, debitCardTransactionTypeCode } = item
      const date = getFormattedDateByJakarta(transactionDate, 'DD MMMM YYYY')

      if (!debitCardTransactionTypeCodeList.includes(debitCardTransactionTypeCode)) {
        return list
      }

      if (!isAll && FILTER_TYPE_BY_TRANSACTION[debitCardTransactionTypeCode] !== filter.type) {
        return list
      }

      if (list[date]) {
        list[date] = [...list[date], item]
      } else {
        list[date] = [item]
      }

      return list
    }, {})
  },
}

export function* reloadDebitCardHistory() {
  try {
    const debitCardNo = yield select(debitCardHistorySelector.debitCardNo)
    const { selectedYearMonth, filter } = yield select(debitCardHistorySelector.view)
    const targetDateString = getFormattedYearMonth(selectedYearMonth.year, selectedYearMonth.month)
    const isAll = filter.type === DebitCardTransactionFilterTypeCode.ALL

    const params: IDebitCardMonthlyTransactionHistoryWithBriefRequest = {
      debitCardNo,
      filter: {
        debitCardTransactionFilterTypeCode: filter.type,
        orderingTypeCode: filter.sort,
      },
      pageInfoRequest: {
        page: 1,
        rowCnt: HistoryListRequestRowCount,
      },
      targetDateString,
    }

    const { monthlyBrief, ...otherRes } = yield call(fetchDebitCardTransactionWithBenefit, params)
    const res = isAll ? { monthlyBrief, ...otherRes } : { ...otherRes }

    yield all([
      put(debitCardHistoryActions.resetPage()),
      put(debitCardHistoryReloadAsync.success(res)),
    ])
  } catch (e) {
    yield put(debitCardHistoryReloadAsync.failure(e))
    yield put(errorActions.set(e))
  }
}

export function* changeYearMonth() {
  try {
    const { selectedYearMonth } = yield select(debitCardHistorySelector.view)
    const { year, month } = selectedYearMonth
    const { result }: IYearMonthInfoResult = yield call(Bridge.getYearMonth, { year, month })

    yield all([
      put(debitCardHistoryActions.setView({ selectedYearMonth: result })),
      call(reloadDebitCardHistory),
    ])
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* getMoreDebitCardHistory() {
  try {
    yield put(loadingActions.start(DEBIT_CARD_HISTORY_MORE))

    const debitCardNo = yield select(debitCardHistorySelector.debitCardNo)
    const { curPage } = yield select(debitCardHistorySelector.pageInfo)
    const { selectedYearMonth, filter } = yield select(debitCardHistorySelector.view)
    const targetDateString = getFormattedYearMonth(selectedYearMonth.year, selectedYearMonth.month)

    const params: IDebitCardMonthlyTransactionHistoryWithBriefRequest = {
      debitCardNo,
      filter: {
        debitCardTransactionFilterTypeCode: filter.type,
        orderingTypeCode: filter.sort,
      },
      pageInfoRequest: {
        page: curPage,
        rowCnt: HistoryListRequestRowCount,
      },
      targetDateString,
    }

    const res = yield call(fetchDebitCardTransactionWithBenefit, params)
    yield put(debitCardHistoryMoreAsync.success(res))
  } catch (e) {
    yield put(debitCardHistoryMoreAsync.failure(e))
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(DEBIT_CARD_HISTORY_MORE))
  }
}

export const debitCardHistoryReducer = handleActions(reducer, initialState)
export const debitCardHistorySaga = [
  takeLatest(
    debitCardHistoryAsync.FETCH,
    createSaga(debitCardHistoryAsync, fetchDebitCardTransactionWithBenefit),
  ),
  takeLatest(debitCardHistoryReloadAsync.POST, reloadDebitCardHistory),
  takeLatest(CHANGE_YEAR_MONTH, changeYearMonth),
  takeLatest(INCREASE_PAGE, getMoreDebitCardHistory),
]
